import { htmlSafe } from '@ember/template';
import { Marked, type TokenizerAndRendererExtension } from 'marked';

// Vanilla instance for rendering without hints (reused across calls)
const vanillaMd = new Marked({ async: false });

/**
 * Represents an item hint to be applied in text.
 */
export interface ItemHint {
  /** The mention ID for scrolling */
  id: string;
  /** The item name to match in text (enriched/corrected title) */
  name: string;
  /** The original extracted name (if different) — also matched */
  extractedName?: string;
  /** The item type for tooltip display */
  type: string;
}

/**
 * Escapes HTML special characters in a string.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * A match entry links a search term back to its parent hint.
 */
interface MatchEntry {
  /** Regex-escaped search term */
  pattern: string;
  /** The hint this entry belongs to */
  hint: ItemHint;
}

/**
 * Builds match entries from hints, sorted longest-first.
 * Each hint may produce up to two entries: one for `name`, one for
 * `extractedName` (when present and different).
 */
function buildMatchEntries(hints: ItemHint[]): MatchEntry[] {
  const entries: MatchEntry[] = [];
  const seen = new Set<string>();

  for (const hint of hints) {
    const nameLower = hint.name.toLowerCase();
    if (!seen.has(nameLower)) {
      seen.add(nameLower);
      entries.push({
        pattern: escapeRegex(hint.name),
        hint,
      });
    }

    if (hint.extractedName) {
      const extLower = hint.extractedName.toLowerCase();
      if (!seen.has(extLower)) {
        seen.add(extLower);
        entries.push({
          pattern: escapeRegex(hint.extractedName),
          hint,
        });
      }
    }
  }

  // Sort longest pattern first so "The Matrix Reloaded" beats "Matrix"
  return entries.sort((a, b) => b.pattern.length - a.pattern.length);
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Creates a Marked inline extension that tokenizes item names
 * and renders them as tooltip spans.
 *
 * Operating at tokenization level means we work on raw markdown
 * text — no HTML entity mismatches, no risk of matching inside
 * tags or attributes.
 */
function createItemHintExtension(
  hints: ItemHint[]
): TokenizerAndRendererExtension {
  const entries = buildMatchEntries(hints);
  if (entries.length === 0) {
    // Return a no-op extension that never matches
    return {
      name: 'itemHint',
      level: 'inline' as const,
      start() {
        return undefined;
      },
      tokenizer() {
        return undefined;
      },
      renderer() {
        return '';
      },
    };
  }

  // Combined regex for start() — finds the earliest potential match
  const combinedPattern = entries.map((e) => e.pattern).join('|');
  const startRegex = new RegExp(combinedPattern, 'i');

  // Per-entry anchored regexes for tokenizer() — tried longest-first
  const anchoredEntries = entries.map((e) => ({
    regex: new RegExp(`^(${e.pattern})`, 'i'),
    hint: e.hint,
  }));

  return {
    name: 'itemHint',
    level: 'inline' as const,
    start(src: string) {
      const match = startRegex.exec(src);
      return match?.index;
    },
    tokenizer(src: string) {
      for (const entry of anchoredEntries) {
        const match = entry.regex.exec(src);
        if (match) {
          return {
            type: 'itemHint',
            raw: match[0],
            text: match[0],
            hintName: entry.hint.name,
            hintType: entry.hint.type,
            hintId: entry.hint.id,
          };
        }
      }
      return undefined;
    },
    renderer(token) {
      const tooltipText = escapeHtml(
        `${token['hintName']} (${token['hintType']})`
      );
      const matchText = escapeHtml(token.raw);
      const mentionId = token['hintId'] as string;
      return (
        `<span class="tooltip tooltip-primary item-hint"` +
        ` data-tip="${tooltipText}"` +
        ` data-mention-id="${mentionId}">${matchText}</span>`
      );
    },
  };
}

/**
 * Parses markdown and wraps item-name occurrences in tooltip spans.
 *
 * When hints are provided, creates a Marked instance with a custom
 * inline extension that recognizes item names at tokenization time.
 * This avoids HTML-entity mismatches and tag-attribute false positives
 * that plague regex-on-HTML approaches.
 *
 * Falls back to a shared vanilla Marked instance when no hints are given.
 */
export default function parseHintedMarkdown(
  text: string | null | undefined,
  hints: ItemHint[]
): ReturnType<typeof htmlSafe> {
  if (!text) return htmlSafe('');

  if (hints.length === 0) {
    return htmlSafe(vanillaMd.parse(text) as string);
  }

  const md = new Marked({ async: false });
  md.use({ extensions: [createItemHintExtension(hints)] });

  return htmlSafe(md.parse(text) as string);
}
